/**
 * bulkCost — preencher/ajustar o CUSTO de vários itens do catálogo de uma vez.
 *
 * POR QUE ISTO EXISTE: o grupo 1 criou `products.cost`, mas nada preenchia em
 * massa. Um catálogo com 200 itens só ganharia custo item a item, e a base de
 * margem/CMV dos grupos seguintes nasceria vazia na prática.
 *
 * DUAS REGRAS QUE QUEBRAM EM SILÊNCIO SE IGNORADAS:
 *
 * 1. **O custo de um produto com variações mora nas FILHAS** (decisão do grupo
 *    1: o pai fica com `cost = null` porque não é comprável nem vendável).
 *    Aplicar em lote no id selecionado escreveria no pai — que não aparece em
 *    relatório nenhum — e o usuário veria "12 itens atualizados" com zero
 *    efeito. Por isso o plano DESCE para as variações.
 * 2. **Ajuste percentual sobre item sem custo é impossível**, não é zero
 *    (`Number(null) === 0` faria "+10%" virar custo 0,00 — armadilha #61).
 *    Esses itens saem em `skipped` e a tela DIZ quantos ficaram de fora.
 *
 * Módulo puro: o I/O fica no modal.
 */
import { productUnitCost } from '@/lib/cost';
import { isVariant, variantDisplayName } from '@/lib/variants';
import { roundMoney } from '@/lib/pricing';

export type BulkCostMode =
  /** Define o mesmo custo em todos. */
  | 'set'
  /** Custo = X% do preço de venda de cada item (semeia o catálogo inteiro). */
  | 'percent_of_price'
  /** Aumenta/diminui X% sobre o custo ATUAL (exige custo já informado). */
  | 'adjust';

export interface BulkCostProduct {
  id: string;
  name?: string | null;
  price?: number | null;
  cost?: number | string | null;
  parent_product_id?: string | null;
  variant_name?: string | null;
}

/** Uma linha que será gravada. */
export interface BulkCostUpdate {
  id: string;
  displayName: string;
  /** Custo antes (para o preview). */
  previousCost: number | null;
  cost: number;
}

export type BulkCostSkipReason =
  /** Ajuste percentual num item que ainda não tem custo. */
  | 'no_cost'
  /** `percent_of_price` num item sem preço utilizável. */
  | 'no_price';

export interface BulkCostSkip {
  id: string;
  displayName: string;
  reason: BulkCostSkipReason;
}

export interface BulkCostPlan {
  updates: BulkCostUpdate[];
  skipped: BulkCostSkip[];
}

export interface BulkCostInput {
  /** Ids selecionados na lista — sempre RAÍZES (a lista só mostra raízes). */
  selectedIds: Iterable<string>;
  /** Catálogo plano do banco (pais e filhas misturados). */
  products: BulkCostProduct[];
  mode: BulkCostMode;
  /** R$ no modo `set`; percentual nos outros (negativo = diminuir). */
  value: number;
}

/**
 * Expande a seleção para as unidades que REALMENTE guardam custo: a variação
 * quando existe, o próprio produto quando não.
 */
export function costBearingTargets(
  selectedIds: Iterable<string>,
  products: BulkCostProduct[],
): BulkCostProduct[] {
  const selected = new Set(Array.from(selectedIds, String));
  const list = products ?? [];

  const childrenByParent = new Map<string, BulkCostProduct[]>();
  for (const p of list) {
    if (!isVariant(p)) continue;
    const key = String(p.parent_product_id);
    const bucket = childrenByParent.get(key);
    if (bucket) bucket.push(p);
    else childrenByParent.set(key, [p]);
  }

  const targets: BulkCostProduct[] = [];
  for (const p of list) {
    if (!selected.has(String(p.id))) continue;
    const children = childrenByParent.get(String(p.id));
    if (children && children.length > 0) targets.push(...children);
    else targets.push(p);
  }
  return targets;
}

/** Nome legível do alvo — a variação aparece como "Camiseta — P". */
function targetName(target: BulkCostProduct, products: BulkCostProduct[]): string {
  if (!isVariant(target)) return String(target.name ?? '');
  const parent = (products ?? []).find((p) => String(p.id) === String(target.parent_product_id));
  return variantDisplayName(parent?.name ?? target.name, target.variant_name);
}

/**
 * Monta o plano de gravação. Não faz I/O e não arredonda percentual: o custo
 * final é arredondado em centavos, que é a precisão que o campo aceita.
 */
export function buildBulkCostPlan({ selectedIds, products, mode, value }: BulkCostInput): BulkCostPlan {
  const targets = costBearingTargets(selectedIds, products);
  const updates: BulkCostUpdate[] = [];
  const skipped: BulkCostSkip[] = [];

  for (const target of targets) {
    const displayName = targetName(target, products);
    const current = productUnitCost(target);

    if (mode === 'set') {
      updates.push({ id: String(target.id), displayName, previousCost: current, cost: roundMoney(value) });
      continue;
    }

    if (mode === 'percent_of_price') {
      const price = Number(target.price ?? 0);
      if (!Number.isFinite(price) || price <= 0) {
        skipped.push({ id: String(target.id), displayName, reason: 'no_price' });
        continue;
      }
      updates.push({
        id: String(target.id),
        displayName,
        previousCost: current,
        cost: roundMoney((price * value) / 100),
      });
      continue;
    }

    // mode === 'adjust'
    if (current == null) {
      // Item sem custo não tem sobre o que aplicar percentual. Tratar como 0
      // gravaria "custo 0,00" (= de graça) em massa, que é exatamente o erro
      // que a feature de custo existe para evitar.
      skipped.push({ id: String(target.id), displayName, reason: 'no_cost' });
      continue;
    }
    const next = roundMoney(current * (1 + value / 100));
    updates.push({
      id: String(target.id),
      displayName,
      previousCost: current,
      // Custo não pode ficar negativo (o banco tem CHECK cost >= 0).
      cost: Math.max(0, next),
    });
  }

  return { updates, skipped };
}

/** Frase pt-BR do que ficou de fora — a tela NUNCA cala uma omissão. */
export function describeSkipped(skipped: BulkCostSkip[]): string | null {
  if (!skipped || skipped.length === 0) return null;
  const noCost = skipped.filter((s) => s.reason === 'no_cost').length;
  const noPrice = skipped.filter((s) => s.reason === 'no_price').length;

  const parts: string[] = [];
  if (noCost > 0) {
    parts.push(
      noCost === 1
        ? '1 item ficou de fora por ainda não ter custo informado'
        : `${noCost} itens ficaram de fora por ainda não terem custo informado`,
    );
  }
  if (noPrice > 0) {
    parts.push(
      noPrice === 1 ? '1 item ficou de fora por não ter preço' : `${noPrice} itens ficaram de fora por não terem preço`,
    );
  }
  return `${parts.join(' e ')}.`;
}
